/**
 * A porta que segura o aluno até a escola liberar a matrícula.
 *
 * Modal SEM saída por clique fora e sem X: enquanto a matrícula não sai, não há
 * o que fazer dentro do app, e um modal que fecha deixaria o aluno num app vazio
 * sem entender por quê. As duas portas que sobram são reais — sair da conta e
 * conferir de novo.
 *
 * Registro: LÚDICO (DESIGN.md) — é o celular do adolescente, e a primeira coisa
 * que ele vê depois de criar a conta. Adesivo no título, tinta clara sobre o
 * navy, mesma família da `/sala` e do `/trocar-senha`.
 *
 * ⚠️ ESTA É A PORTA DE DENTRO. A de fora é o `class-room`, que recusa aluno
 * pendente na entrada da sala — sem ela, o QR do telão furaria esta trava. As
 * duas precisam existir: esta explica, aquela impede.
 */
import { useState } from 'react';
import { Loader2, Clock, KeyRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Adesivo } from '@/components/ui/adesivo';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useMatricula } from '@/hooks/useMatricula';
import { useIdentidade } from '@/hooks/useIdentidade';
import { lerRespostaDaFunction } from '@/lib/respostaDaFunction';
import { toast } from 'sonner';

export function PortaDaMatricula({ children }: { children: React.ReactNode }) {
  const { estado, turmaPendente, recarregar } = useMatricula();
  const { signOut } = useAuth();
  const { nome } = useIdentidade();
  const [codigo, setCodigo] = useState('');
  const [enviando, setEnviando] = useState(false);

  if (estado === 'carregando' || estado === 'liberado') return <>{children}</>;

  const pedirEntrada = async () => {
    if (!codigo.trim()) return toast.error('Digite o código da turma');
    setEnviando(true);
    const { data, error } = await supabase.functions.invoke('matricula', {
      body: { action: 'inscrever', codigo: codigo.trim(), full_name: nome ?? '' },
    });
    setEnviando(false);
    const r = await lerRespostaDaFunction(data, error);
    if (!r || r.error) return toast.error(r?.error ?? 'Não deu pra enviar o pedido agora');
    toast.success(`Pedido enviado para ${r.turma}`);
    setCodigo('');
    recarregar();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm text-center">
        {estado === 'pendente' ? (
          <>
            <Adesivo cor="azul" tinta="clara" className="mx-auto mb-6 px-5 py-2">
              <span className="font-marca text-lg font-extrabold uppercase">
                Quase lá
              </span>
            </Adesivo>
            <Clock className="mx-auto mb-4 h-10 w-10 text-primary" aria-hidden="true" />
            <h1 className="text-xl font-bold">Aguardando ativação de matrícula</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Seu pedido chegou{turmaPendente ? <> para <strong>{turmaPendente}</strong></> : null}.
              Assim que a escola liberar, você entra direto.
            </p>
            {/* Botão que RECONFERE, não que recarrega a página: o aluno vai
                apertar isso várias vezes enquanto espera, e reload no celular
                com wifi de escola é meio minuto olhando tela branca. */}
            <Button className="mt-6 w-full" onClick={recarregar}>
              Conferir de novo
            </Button>
          </>
        ) : (
          <>
            <Adesivo cor="azul" tinta="clara" className="mx-auto mb-6 px-5 py-2">
              <span className="font-marca text-lg font-extrabold uppercase">
                Falta a turma
              </span>
            </Adesivo>
            <KeyRound className="mx-auto mb-4 h-10 w-10 text-primary" aria-hidden="true" />
            <h1 className="text-xl font-bold">Qual é o código da sua turma?</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Sua conta existe, mas ainda não está ligada a nenhuma turma. O código
              está com o seu professor.
            </p>
            <div className="mt-6 text-left">
              <Label htmlFor="codigo-turma">Código da turma</Label>
              <Input
                id="codigo-turma"
                value={codigo}
                onChange={(e) => setCodigo(e.target.value.toUpperCase())}
                placeholder="Ex.: 5EXDRB"
                autoCapitalize="characters"
                autoComplete="off"
                className="mt-1 text-center font-mono text-lg tracking-widest"
                maxLength={12}
              />
            </div>
            <Button className="mt-4 w-full" onClick={pedirEntrada} disabled={enviando}>
              {enviando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Pedir entrada
            </Button>
          </>
        )}

        <Button variant="ghost" className="mt-3 w-full" onClick={signOut}>
          Sair da conta
        </Button>
      </div>
    </div>
  );
}
